import Component from '../core/Component'
import EventBus from '../core/EventBus'
import { EVENTS } from '../core/Events'
import {substitute} from '../utils/String'

const MAX_PREVIEW_BUFFER = 1

export default class ProductGallery extends Component {
    constructor(element, options = {}) {
        super(element, options)

        this.ref = {
            preview: null,
            thumbList: null,
            thumbs: [],
            thumbTemplate: null,
            prev: null,
            next: null
        }

        this.lastSelectedIndex = 0
        this.lastFadedIndex = null
    }

    prepare() {
        this.ref.thumbList.addEventListener('scroll', this.handleThumbListScroll)
        this.ref.prev?.addEventListener('click', this.handlePrevClick)
        this.ref.next?.addEventListener('click', this.handleNextClick)
        this.attachThumbListeners()
        this.updatePrevNext()
        this.centerPreviewImage()
        this.checkCount()

        this.element.addEventListener('focus', this.handleFocus)

        EventBus.on(EVENTS.MODAL_OPEN, this.handleModalOpen)
    }

    destroy() {
        EventBus.off(EVENTS.MODAL_OPEN, this.handleModalOpen)
    }

    handleKeydown = (event) => {
        if (event.key === 'ArrowRight') {
            this.selectNext()
        } else if (event.key === 'ArrowLeft') {
            this.selectPrev()
        }
    }

    handleFocus = () => {
        this.attachKeyboard()
    }

    handleBlur = () => {
        this.detachKeyboard()
    }

    handleModalOpen = (event) => {
        if (!event.modal.element.contains(this.element)) {
            return
        }
        this.select(event.data.index)
        this.centerPreviewImage()
        this.element.focus()
    }

    handleThumbListScroll = () => {
        this.updateFadedTarget()
    }

    handleThumbClick = (event, index) => {
        this.select(index)
    }

    handleThumbMouseOver = (event, index) => {
        this.select(index)
    }

    handlePrevClick = () => {
        this.selectPrev()
    }

    handleNextClick = () => {
        this.selectNext()
    }

    attachThumbListeners() {
        this.ref.thumbs.forEach((thumb, index) => thumb.addEventListener('mouseover', (event) => this.handleThumbMouseOver(event, index)))
        this.ref.thumbs.forEach((thumb, index) => thumb.addEventListener('click', (event) => this.handleThumbClick(event, index)))
    }

    attachKeyboard() {
        if (this.isKeyboardAttached) {
            return
        }

        document.addEventListener('keydown', this.handleKeydown)
        this.isKeyboardAttached = true
    }

    detachKeyboard() {
        document.removeEventListener('keydown', this.handleKeydown)
        this.isKeyboardAttached = false
    }

    updatePrevNext() {
        this.element.classList.toggle('is-prev-visible', this.lastSelectedIndex !== 0)
        this.element.classList.toggle('is-next-visible', this.lastSelectedIndex < this.ref.thumbs.length - 1)
    }

    selectPrev() {
        let index = this.lastSelectedIndex - 1
        if (index < 0) {
            index = 0
        }
        this.select(index)
    }

    selectNext() {
        let index = this.lastSelectedIndex + 1
        if (index >= this.ref.thumbs.length) {
            index = this.ref.thumbs.length - 1
        }
        this.select(index)
    }

    select(index) {
        if (this.lastSelectedIndex === index) {
            return
        }

        this.select(index)
    }

    select = (index) => {
        const thumb = this.ref.thumbs[index]
        if (!thumb) {
            return
        }

        const img = document.createElement('img')
        img.src = thumb.dataset.src
        img.style.opacity = '0'
        img.style.transition = '.3s opacity'
        img.width = 720
        img.height = 620
        this.ref.preview.appendChild(img)

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                img.style.opacity = '1'
            })
        })

        if (this.ref.preview.children.length > MAX_PREVIEW_BUFFER) {
            this.ref.preview.removeChild(this.ref.preview.firstElementChild)
        }

        this.ref.preview.setAttribute('data-data', JSON.stringify({ index }))
        this.lastSelectedIndex = index
        this.updatePrevNext()
    }

    updateFadedTarget() {
        const firstThumb = this.ref.thumbs[0]
        if (!firstThumb) {
            return
        }

        const thumbHeight = firstThumb.offsetHeight

        const index = Math.floor(this.ref.thumbList.scrollTop / thumbHeight) + 3
        if (this.lastFadedIndex === index) {
            return
        }

        this.ref.thumbs.forEach(thumb => thumb.classList.remove('is-faded'))
        this.ref.thumbs[index]?.classList.add('is-faded')
        this.lastFadedIndex = index
    }

    checkCount() {
        this.element.classList.toggle('is-oneitem', this.ref.thumbs.length === 1)
    }

    centerPreviewImage() {
        this.ref.preview.scrollTop = (this.ref.preview.scrollHeight - this.ref.preview.offsetHeight) / 2
        this.ref.preview.scrollLeft = (this.ref.preview.scrollWidth - this.ref.preview.offsetWidth) / 2
    }

    setImages(srcs) {
        const images = srcs.map(src => createProductImageAttributes(src))
        const html = images.map(image => substitute(this.ref.thumbTemplate.innerText, image)).join('')
        this.ref.thumbList.innerHTML = html
        this.ref.thumbs = [...this.element.querySelectorAll('[data-ref="thumb"]')]
        this.attachThumbListeners()

        this.select(0)
        this.checkCount()
    }
}


function splitStringAtIndex(value, index) {
    return [value.substring(0, index), value.substring(index)]
}

function createProductImageAttributes(imageSrc) {
    let index = imageSrc.lastIndexOf('.')
    const [src, extension] = splitStringAtIndex(imageSrc, index)

    return {
        src: `${src}.180x180.fit.q85${extension}`,
        srcPreview: `${src}.1600x1600.fit.q65${extension}`,
        srcset: `
        ${src}.70x70.fit.q85${extension} 70w,
        ${src}.140x140.fit.q85${extension} 140w,
        ${src}.180x180.fit.q85${extension} 180w,
        ${src}.360x360.fit.q85${extension} 360w,
        ${src}.800x800.fit.q85${extension} 800w,
        ${src}.1600x1600.fit.q65${extension} 1600w
        `
    }
}